import * as React from 'react'
import { ReactElement, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { DialogContent, makeStyles, mergeClasses, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { HammockFormDialogProps } from '../HammockFormDialog'
import * as Yup from 'yup'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { TimeMutationDto, TimeOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import FileCombobox, { FileComboboxProps } from '../../../../components/Combobox/FileCombobox'
import EnumMapCombobox, { EnumMapComboboxProps } from '../../../../components/Combobox/EnumMapCombobox'
import { useSettings } from '../../../../hooks/useSettings'
import useModules from '../../../../hooks/module/useModules'
import { useTimeCategoriesSetting } from '../../../../hooks/file/useTimeCategoriesSetting'
import { useTimeDivisorSetting } from '../../../../hooks/file/useTimeDivisorSetting'
import { FileRateType, ModuleType, TimeCategory } from '../../../../context/GlobalContext'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { BudgetMonitoringDto, IFileDto, RateDto } from '../../../../config/fileConfig'
import { useFormikContext } from 'formik'
import FormikTextareaField from '../../../../components/Formik/FormikTextareaField'
import { Billable } from '../../../../config/registration/timeConfig'
import OverflowButtonPanel from '../../../../components/Overflow/OverflowButtonPanel'
import {
    formatInlineCurrency,
    formatTimeInMinutes,
    multiplyTime,
    multiplyTimeNumber,
    parseNumber,
    parseTime,
    parseTimeInHours,
    parseTimeInMinutes,
} from '../../../../helpers/numberHelper'
import UserCombobox from '../../../../components/Combobox/UserCombobox'
import useRoles, { rolesWriteAllTimes } from '../../../../hooks/useRoles'
import { SubComboboxProps } from '../../../../components/Combobox/BaseCombobox'
import { UserListItemDto } from '../../../../config/userConfig'
import FormikDurationField from '../../../../components/Formik/FormikDurationField'
import LocalDatePicker, { LocalDatePickerProps } from '../../../../components/DatePicker/LocalDatePicker'
import FormikField, { useFormikFieldStyles } from '../../../../components/Formik/FormikField'
import { usePrevious } from '@fluentui/react-hooks'
import { IRefObject } from '@fluentui/utilities'
import { ITextField } from '@fluentui/react'
import { BudgetMonitoringModuleDto, useFileModule } from '../../../../hooks/module/useFileModule'
import { BaseTierDto, isActiveTier, isBaseUserTier } from '../../../../config/finance/financeConfig'
import useCurrency from '../../../../hooks/useCurrency'
import { parseDate } from '../../../../helpers/dateHelper'
import FormikMoneyField from '../../../../components/Formik/FormikMoneyField'
import BillableCombobox, { BillableComboboxProps } from '../../../../components/Combobox/BillableCombobox'
import FormContainer from '../../Form/FormContainer'

const useStyles = makeStyles({
    input: {
        minWidth: '80px',
    },
    billable: {
        flexGrow: 2,
    },
    rateRow: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRow: {
        display: 'grid',
        gridTemplateColumns: `1fr 2fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRowSmall: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
})

export const getValidationSchema = (hasTimeCategoryModule: boolean, translate: (key: string, options?: any) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        category: hasTimeCategoryModule
            ? Yup.string().label(translate('time.category.label')).required()
            : Yup.string().label(translate('time.category.label')).nullable(),
        description: Yup.string().label(translate('time.description')).required(),
        fileId: Yup.string().uuid().label(translate('time.fileid')).required(),
        duration: Yup.string()
            .label(translate('time.duration.label'))
            .required()
            .test('is-valid', translate('time.duration.isvalid'), value => !isNaN(parseTimeInMinutes(value)))
            .test('is-valid-minutes', translate('time.duration.isvalidminutes'), value => parseTime(value).minutes < 60)
            .test('is-less-than-a-minute', translate('time.duration.islessminute'), value => parseTimeInMinutes(value) >= 1)
            .test('is-more-than-a-day', translate('time.duration.ismoreday'), value => parseTimeInMinutes(value) <= 24 * 60),
        rate: Yup.string().label(translate('time.rate')).required(),
        userId: Yup.string().uuid().label(translate('time.userid')).required(),
        date: Yup.string().label(translate('time.date')).required(),
        billable: Yup.string().label(translate('time.billable')).required(),
    })
}

const getRateValue = (initialValues: TimeMutationDto, values: TimeMutationDto, rates: RateDto<BaseTierDto>): string => {
    if (
        values.userId === initialValues.userId &&
        values.fileId === initialValues.fileId &&
        values.billable === initialValues.billable &&
        values.billable === Billable.Billable &&
        values.date === initialValues.date &&
        initialValues.rate
    ) {
        // If nothing of interest has changed from the initial form, inherit the initial value.
        return initialValues.rate
    }
    if (values.billable !== Billable.Billable) {
        return '0.00'
    }

    switch (rates.fileRateType) {
        case FileRateType.File:
            return rates.rate
        case FileRateType.UserTier:
            return (
                (rates.tiers || [])
                    .filter(tier => isActiveTier(tier, parseDate(values.date)))
                    .filter(tier => isBaseUserTier(tier))
                    .find(tier => tier.userId === values.userId)?.salesRate || '0.00'
            )
        case FileRateType.PartyTier:
            return (rates.tiers || []).find(tier => isActiveTier(tier, parseDate(values.date)))?.salesRate || '0.00'
    }
}

const getBillableValue = (
    initialValues: TimeMutationDto,
    values: TimeMutationDto,
    rates: RateDto<BaseTierDto>,
    timeCategories: TimeCategory[],
): Billable => {
    if (values.fileId === initialValues.fileId && values.category === initialValues.category && initialValues.billable) {
        // If nothing of interest has changed from the initial form, inherit the initial value.
        return initialValues.billable
    }

    const category = timeCategories?.find(category => category.key === values.category)
    return category?.billable ?? rates?.billable ?? values.billable ?? initialValues.billable
}

export default function TimeMutationDialogBodyContent(
    props: HammockFormDialogProps<TimeMutationDto, BaseMutableOverviewDialogProps<TimeOverviewDto>> & {
        focusTrigger?: number
        updateFocusTrigger?: () => void
    },
): ReactElement {
    const axios = useAxios()
    const modules = useModules()

    const roles = useRoles()
    const { settings } = useSettings()
    const styles = useStyles()
    const formikFieldStyles = useFormikFieldStyles()
    const timeDivisorSetting = useTimeDivisorSetting()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const { initialValues, values, setFieldValue } = useFormikContext<TimeMutationDto>()
    const previousValues = usePrevious<TimeMutationDto>(values)

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<TimeOverviewDto>), [props])

    const {
        hasTimeCategoriesModule: fileHasTimeCategoriesModule,
        timeCategories,
        defaultCategory: defaultTimeCategory,
        setCurrentFile: setTimeCategoriesFile,
    } = useTimeCategoriesSetting()

    const timeCategoryOptions = useMemo<Map<string, string>>(
        () => new Map(timeCategories?.map(x => [x.key, x.name] as [string, string])),
        [timeCategories],
    )

    const hasTimeCategoriesModule = useMemo<boolean>(
        () =>
            (fileHasTimeCategoriesModule || settings?.modules?.find(mod => mod.type === ModuleType.TimeCategories)?.defaultActiveForFile) &&
            settings?.modules?.some(mod => mod.type === ModuleType.TimeCategories),
        [fileHasTimeCategoriesModule, settings],
    )

    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])

    const isInvoiceDraft: boolean = dialogBodyProps.item?.invoice?.status === InvoiceStatus.Draft || false
    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    const {
        fileModule: budgetMonitoringModule,
        has: hasBudgetMonitoringModule,
        setCurrentFile: setBudgetMonitoringModuleFile,
    } = useFileModule<BudgetMonitoringModuleDto>(ModuleType.BudgetMonitoring)

    //set fixed rate, but replace value once settings have been loaded
    const [rates, setRates] = useState<RateDto<BaseTierDto>>({
        fileRateType: FileRateType.File,
        rate: '0.00',
        currency: 'EUR',
    })
    const { currency } = useCurrency(values.currency)
    const currencySymbol = useMemo<string>(() => currency?.symbol ?? currency?.alpha ?? '¤', [currency])
    const [budgetMonitoring, setBudgetMonitoring] = useState<BudgetMonitoringDto>(undefined)

    //Check if the category field should use the initial value or live value
    const useInitialCategory = useMemo<boolean>(
        () => !!initialValues.category && initialValues.fileId === values.fileId,
        [initialValues, values.fileId],
    )
    const useInitialDescription = useMemo<boolean>(
        () => !!initialValues.description && initialValues.fileId === values.fileId && initialValues.category === values.category,
        [initialValues, values.fileId, values.category],
    )
    //Check if the billable field should use the initial value or calculated value
    const useInitialBillable = useMemo<boolean>(
        () => !!initialValues.billable && initialValues.fileId === values.fileId && initialValues.category === values.category,
        [initialValues, values.fileId, values.category],
    )

    const fileFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const categoryFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const descriptionFocusRef: RefObject<ITextField> = useRef<ITextField>()
    const durationFocusRef: RefObject<ITextField> = useRef<ITextField>()

    useEffect(() => {
        if (previousValues && previousValues?.fileId !== values.fileId && !!values.category) {
            setFieldValue('category', undefined)
            return
        }
        if (isInvoiceDraft || isInvoiceFinal) {
            return
        }

        if (!values.fileId) {
            fileFocusRef.current?.focus()
        } else if (!!categoryFocusRef.current && !values.category) {
            categoryFocusRef.current?.focus()
        } else if (!!initialValues.description || !values.description) {
            //Focus on description when in edit mode or when empty in create mode.
            descriptionFocusRef.current?.focus()
        } else {
            durationFocusRef.current?.focus()
        }
    }, [values.fileId, values.category, props.focusTrigger, hasTimeCategoriesModule])

    useEffect(() => {
        setFieldValue('rate', initialValues.rate)
        setFieldValue('rateTotal', multiplyTimeNumber(initialValues.duration, initialValues.rate, '0.00', 'en', 'en'))
        setFieldValue('billable', initialValues.billable)
    }, [initialValues])

    useEffect(() => {
        setFieldValue('rateTotal', multiplyTimeNumber(values.duration, values.rate, '0.00', 'en', 'en'))
    }, [values.rate, values.duration])

    useEffect(() => {
        if (values.fileId) {
            axios.get<IFileDto>(`/file/${values.fileId}`).then(response => {
                setBudgetMonitoringModuleFile(response.data)
                setTimeCategoriesFile(response.data)
            })
        }
    }, [values.fileId])

    useEffect(() => {
        // Always set the rate globally first.
        setRates({
            fileRateType: settings?.fileScaffold?.fileRate?.type || FileRateType.File,
            rate: settings?.fileScaffold?.fileRate?.hourRate || '0.00',
            currency: settings?.fileScaffold?.currency || 'EUR',
        })
        if (values.fileId) {
            // Fetch the rate when the file has changed.
            axios.get<RateDto<BaseTierDto>>(`/file/${values.fileId}/rate`).then(response => {
                setRates(response.data)
            })
        }
    }, [values.fileId])

    useEffect(() => {
        if (rates) {
            setFieldValue('rate', getRateValue(initialValues, values, rates))
            setFieldValue('currency', rates.currency)
        }
    }, [rates, values.userId, values.billable, values.date, values.duration])

    useEffect(() => {
        if (rates) {
            setFieldValue('billable', useInitialBillable ? initialValues.billable : getBillableValue(initialValues, values, rates, timeCategories))
        }
    }, [rates, values.fileId, values.category])

    useEffect(() => {
        if (hasBudgetMonitoringModule) {
            // Fetch the outstanding times when the file has changed.
            axios.get<BudgetMonitoringDto>(`/file/${values.fileId}/budget/monitoring`).then(response => {
                setBudgetMonitoring(response.data)
            })
        }
    }, [hasBudgetMonitoringModule])

    const exceedsBudget = useMemo<boolean>(() => {
        let currentAmount = 0
        if (budgetMonitoringModule && budgetMonitoringModule.hourThreshold > 0 && budgetMonitoring && values.duration) {
            currentAmount = budgetMonitoring.outstandingHours
            if (budgetMonitoringModule.includeBilled) {
                currentAmount += budgetMonitoring.billedHours
            }

            currentAmount += Math.max(0, parseTimeInMinutes(values.duration) / 60 - parseTimeInMinutes(initialValues?.duration || '0:00') / 60)
            if (currentAmount > budgetMonitoringModule.hourThreshold) {
                return true
            }
        }

        currentAmount = 0
        if (budgetMonitoringModule && parseNumber(budgetMonitoringModule.priceThreshold, 'en') > 0 && budgetMonitoring && values.duration) {
            if (budgetMonitoringModule.includeHours) {
                currentAmount += parseNumber(budgetMonitoring.outstandingHoursPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedHoursPrice, 'en')
                }
            }
            if (budgetMonitoringModule.includeTravels) {
                currentAmount += parseNumber(budgetMonitoring.outstandingTravelsPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedTravelsPrice, 'en')
                }
            }
            if (budgetMonitoringModule.includeDisbursements) {
                currentAmount += parseNumber(budgetMonitoring.outstandingDisbursementsPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedDisbursementsPrice, 'en')
                }
            }

            currentAmount += Math.max(
                0,
                multiplyTime(values.duration, parseNumber(values.rate, 'en')) -
                    multiplyTime(initialValues?.duration || '0:00', parseNumber(initialValues?.rate || '0.00', 'en')),
            )

            return currentAmount > parseNumber(budgetMonitoringModule.priceThreshold, 'en')
        }

        return false
    }, [hasBudgetMonitoringModule, budgetMonitoring, values.duration])

    const durationDeviatesFromTimeDivisor = useMemo<boolean>(() => {
        if (!timeDivisorSetting?.timeDivisor || timeDivisorSetting?.timeDivisor < 2) {
            return false
        }

        const time = parseTimeInMinutes(values.duration)
        return time > 0 && time % settings?.timeDivisor > 0
    }, [values.duration, settings])

    useEffect(() => {
        props.setValidationSchema?.(getValidationSchema(hasTimeCategoriesModule, translate))

        if (hasTimeCategoriesModule) {
            const defaultCategory = defaultTimeCategory(initialValues.emailInbound)
            const currentCategoryKey = useInitialCategory ? initialValues.category : (values.category ?? defaultCategory?.key)
            setFieldValue('category', currentCategoryKey)
        } else {
            setFieldValue('category', useInitialCategory ? initialValues.category : undefined)
        }
    }, [hasTimeCategoriesModule, timeCategories])

    useEffect(() => {
        let description = values.description
        if (useInitialDescription) {
            description = initialValues.description
        } else {
            const currentCategory = (timeCategories || []).find(cat => cat.key === values.category)
            if (currentCategory?.defaultDescription && currentCategory?.appendDescription && values.description?.length > 0) {
                description = `${values.description} / ${currentCategory?.defaultDescription}`
            } else if (currentCategory?.defaultDescription) {
                description = currentCategory?.defaultDescription
            }
        }

        setFieldValue('description', description)
    }, [hasTimeCategoriesModule, timeCategories, values.category])

    const durationSuggestions = useMemo<string[]>(() => {
        if (!timeDivisorSetting.timeDivisor || timeDivisorSetting.timeDivisor < 2) {
            return []
        }

        return [
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 2),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 3),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 4),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 5),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 6),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 7),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 8),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 9),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 10),
        ]
    }, [timeDivisorSetting])

    return (
        <DialogContent>
            <FormContainer>
                {isInvoiceDraft && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('time.edit.form.invoice.draft')}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceFinal && (
                    <MessageBar intent={'error'} layout={'multiline'}>
                        <MessageBarBody>{translate('time.edit.form.invoice.final')}</MessageBarBody>
                    </MessageBar>
                )}
                {budgetMonitoringModule && budgetMonitoring && (
                    <>
                        {exceedsBudget && (
                            <MessageBar intent={'error'}>
                                <MessageBarBody>{translate('time.edit.form.budget.hours.exceeds')}</MessageBarBody>
                            </MessageBar>
                        )}
                        <MessageBar intent={'info'}>
                            <MessageBarBody>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: [
                                            translate('time.edit.form.budget.info.title'),
                                            budgetMonitoringModule.includeHours && budgetMonitoringModule.includeBilled
                                                ? translate('time.edit.form.budget.info.billedhours', {
                                                      billedHours: parseTimeInHours(budgetMonitoring.billedHours),
                                                      billedHoursPrice: formatInlineCurrency(budgetMonitoring.billedHoursPrice, currency),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeHours
                                                ? translate('time.edit.form.budget.info.outstandinghours', {
                                                      outstandingHours: parseTimeInHours(budgetMonitoring.outstandingHours),
                                                      outstandingHoursPrice: formatInlineCurrency(budgetMonitoring.outstandingHoursPrice, currency),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeTravels && budgetMonitoringModule.includeBilled
                                                ? translate('time.edit.form.budget.info.billedtravels', {
                                                      ...budgetMonitoring,
                                                      billedTravelsPrice: formatInlineCurrency(budgetMonitoring.billedTravelsPrice, currency),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeTravels
                                                ? translate('time.edit.form.budget.info.outstandingtravels', {
                                                      ...budgetMonitoring,
                                                      outstandingTravelsPrice: formatInlineCurrency(
                                                          budgetMonitoring.outstandingTravelsPrice,
                                                          currency,
                                                      ),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeDisbursements && budgetMonitoringModule.includeBilled
                                                ? translate('time.edit.form.budget.info.billeddisbursements', {
                                                      ...budgetMonitoring,
                                                      billedDisbursementsPrice: formatInlineCurrency(
                                                          budgetMonitoring.billedDisbursementsPrice,
                                                          currency,
                                                      ),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeDisbursements
                                                ? translate('time.edit.form.budget.info.outstandingdisbursements', {
                                                      ...budgetMonitoring,
                                                      outstandingDisbursementsPrice: formatInlineCurrency(
                                                          budgetMonitoring.outstandingDisbursementsPrice,
                                                          currency,
                                                      ),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.hourThreshold > 0
                                                ? translate('time.edit.form.budget.info.hourthreshold', {
                                                      ...budgetMonitoringModule,
                                                  })
                                                : undefined,
                                            parseNumber(budgetMonitoringModule.priceThreshold, 'en') > 0
                                                ? translate('time.edit.form.budget.info.pricethreshold', {
                                                      ...budgetMonitoringModule,
                                                      priceThreshold: formatInlineCurrency(budgetMonitoringModule.priceThreshold, currency),
                                                  })
                                                : undefined,
                                        ]
                                            .filter(Boolean)
                                            .join('<br/>'),
                                    }}
                                ></div>
                            </MessageBarBody>
                        </MessageBar>
                    </>
                )}
                {durationDeviatesFromTimeDivisor && (
                    <MessageBar intent={'warning'}>
                        <MessageBarBody>{translate('time.edit.form.divisor', { divisor: settings?.timeDivisor })}</MessageBarBody>
                    </MessageBar>
                )}

                <FormikComboboxField<TimeMutationDto, FileComboboxProps>
                    required
                    fieldName={'fileId'}
                    component={FileCombobox}
                    componentProps={{
                        readOnly: isInvoiceFinal || isInvoiceDraft,
                        disabled: isInvoiceFinal || isInvoiceDraft,
                        componentRef: fileFocusRef,
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                {hasTimeCategoriesModule && (
                    <FormikComboboxField<TimeMutationDto, EnumMapComboboxProps<string>>
                        required={hasTimeCategoriesModule}
                        fieldName={'category'}
                        component={EnumMapCombobox<string>}
                        componentProps={{
                            values: timeCategoryOptions,
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal || !values.fileId,
                            placeholderKey: 'time.category.placeholder',
                            componentRef: categoryFocusRef,
                            onKeyDown: e => {
                                if (e.key === 'Tab') {
                                    e.preventDefault()
                                    props.updateFocusTrigger()
                                }
                            },
                        }}
                    />
                )}
                <FormikTextareaField<TimeMutationDto>
                    fieldName="description"
                    componentProps={{
                        required: true,
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                        placeholder: 'Omschrijving',
                        ref: descriptionFocusRef,
                    }}
                    className={formikFieldStyles.tinyLabel}
                />
                <FormikComboboxField<TimeMutationDto, SubComboboxProps<UserListItemDto>>
                    required
                    fieldName={'userId'}
                    component={UserCombobox}
                    componentProps={{
                        readOnly: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        disabled: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                {durationSuggestions.length > 0 && !(isInvoiceFinal || isInvoiceDraft) && (
                    <OverflowButtonPanel
                        data={durationSuggestions}
                        overflowAxis={'horizontal'}
                        onItemClick={value => setFieldValue('duration', value)}
                    />
                )}
                <div className={styles.rateRow}>
                    <FormikDurationField<TimeMutationDto>
                        required
                        fieldName={'duration'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            ref: durationFocusRef,
                            className: styles.input,
                        }}
                    />
                    {modules.has(ModuleType.Invoices) && (
                        <>
                            <FormikMoneyField<TimeMutationDto>
                                required
                                fieldName={'rate'}
                                componentProps={{
                                    readOnly: isInvoiceFinal || values.billable !== Billable.Billable,
                                    disabled: isInvoiceFinal || values.billable !== Billable.Billable,
                                    contentBefore: currencySymbol,
                                    className: styles.input,
                                }}
                            />
                            <FormikMoneyField<TimeMutationDto>
                                required
                                fieldName={'rateTotal'}
                                componentProps={{
                                    readOnly: true,
                                    disabled: isInvoiceFinal,
                                    tabIndex: -1,
                                    appearance: 'filled-darker',
                                    contentBefore: currencySymbol,
                                    className: styles.input,
                                }}
                            />
                        </>
                    )}
                </div>
                <div className={mergeClasses(styles.dateRow, viewportSmall && styles.dateRowSmall)}>
                    <FormikField<LocalDatePickerProps, TimeMutationDto>
                        required
                        fieldName={'date'}
                        component={LocalDatePicker}
                        componentProps={{
                            isoValue: values.date,
                            onSelectDate: date => setFieldValue('date', date),
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            root: { className: styles.input },
                        }}
                    />
                    <FormikComboboxField<TimeMutationDto, BillableComboboxProps>
                        required
                        fieldName={'billable'}
                        component={BillableCombobox}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            onKeyDown: e => {
                                if (e.key === 'Tab') {
                                    e.preventDefault()
                                    props.updateFocusTrigger()
                                }
                            },
                        }}
                        className={mergeClasses(styles.input, !viewportSmall && styles.billable)}
                    />
                </div>
                {/*Source - https://stackoverflow.com/a/477699*/}
                <input type="submit" hidden />
            </FormContainer>
        </DialogContent>
    )
}
